import {handleRepositoryDataError} from '@github-ui/data-router-error-helpers'
import {applyIssueFieldValue} from '@github-ui/issue-metadata/issue-field-value-optimistic'
import {useOptimisticData} from '@github-ui/optimistic-connection/useOptimisticData'
import {
  type IssueViewerPayload,
  issueViewerPrimaryQueryOptions,
} from '@github-ui/preheat-issues-manager/issue-viewer-query'
import {useQuery} from '@github-ui/react-query'
import {useMemo} from 'react'

import {DataRouterIssueViewer} from './DataRouterIssueViewer'
import {IssueViewerLoading} from './IssueViewerLoading'
import type {OptionConfig} from './OptionConfig'
import {useTimelinePrefill} from './timeline/timeline-prefill'

const PROJECT_ITEMS_CONNECTION_PATH = ['projectItems']

type TanStackIssueViewerProps = {
  owner: string
  repo: string
  number: number
  optionConfig: OptionConfig
}

export function TanStackIssueViewer({owner, repo, number, optionConfig}: TanStackIssueViewerProps) {
  const queryOptions = useMemo(() => issueViewerPrimaryQueryOptions({owner, repo, number}), [owner, repo, number])
  const query = useQuery(queryOptions)
  const payload = query.data as IssueViewerPayload | undefined
  const mainQueryTargets = useMemo(() => [{queryKey: queryOptions.queryKey}], [queryOptions.queryKey])
  const issueId = payload?.data?.repository?.issue?.id
  const projectItemsConnections = useMemo(
    () => (issueId ? [{ownerId: issueId, connectionPath: PROJECT_ITEMS_CONNECTION_PATH}] : []),
    [issueId],
  )
  const renderedPayload = useOptimisticData(payload, {
    connections: projectItemsConnections,
    targets: mainQueryTargets,
    applyEntityFieldItem: applyIssueFieldValue,
  })

  const repository = renderedPayload?.data?.repository
  const issue = repository?.issue
  const timelineFrontIssue = useMemo(
    () => (issue?.timelineItems ? {id: issue.id, timelineItems: issue.timelineItems} : undefined),
    [issue],
  )
  const timelineBackIssue = useMemo(
    () => (issue?.backTimelineItems ? {id: issue.id, timelineItems: issue.backTimelineItems} : undefined),
    [issue],
  )
  const timelineCoordinates = useMemo(() => ({owner, repo, number}), [owner, repo, number])
  const initialBackAnchor = useTimelinePrefill({
    coordinates: timelineCoordinates,
    issue: timelineFrontIssue,
    backIssue: timelineBackIssue,
    payloadUpdatedAt: query.dataUpdatedAt,
  })

  if (query.isPending) return <IssueViewerLoading optionConfig={optionConfig} />
  if (query.isError) throw query.error

  const data = handleRepositoryDataError(renderedPayload)
  const resolvedRepository = data?.repository
  const resolvedIssue = resolvedRepository?.issue
  if (!resolvedIssue) throw new Error(`Issue missing from repository data for ${owner}/${repo}#${number}`)

  return (
    <DataRouterIssueViewer
      key={resolvedIssue.id}
      issue={resolvedIssue as unknown as Parameters<typeof DataRouterIssueViewer>[0]['issue']}
      initialBackAnchor={initialBackAnchor}
      viewer={data?.safeViewer ?? null}
      isRepoOwnerEnterpriseManaged={resolvedRepository?.isOwnerEnterpriseManaged}
      userNameDisplayConfiguration={resolvedRepository.owner.userNameDisplayConfiguration}
      routeQueryKey={queryOptions.queryKey}
      optionConfig={optionConfig}
    />
  )
}
